import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import { batchAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/dateUtils';

const BatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: batch, isLoading, error } = useQuery({
    queryKey: ['batch', id],
    queryFn: () => {
      if (!id || id === 'undefined') {
        throw new Error('Invalid batch ID');
      }
      return batchAPI.getById(id).then(res => res.data);
    },
    enabled: !!id && id !== 'undefined',
  });

  if (!id || id === 'undefined') {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Invalid batch ID. Please select a valid batch.</p>
        <button
          onClick={() => navigate('/batches')}
          className="mt-2 text-blue-400 hover:text-blue-300"
        >
          Back to Batches
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Error loading batch: {error?.message || 'Batch not found'}</p>
        <button
          onClick={() => navigate('/batches')}
          className="mt-2 text-blue-400 hover:text-blue-300"
        >
          Back to Batches
        </button>
      </div>
    );
  }

  const batchId = batch._id || batch.id;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/batches')}
          className="flex items-center text-gray-300 hover:text-gray-100 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Batches
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">{batch.batch_name}</h1>
            <p className="text-gray-300 mt-2">Subject: {batch.subject}</p>
          </div>
          <button
            onClick={() => navigate(`/batches/${batchId}/edit`)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            <PencilIcon className="h-5 w-5 mr-2" />
            Edit Batch
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Batch Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300">Batch Name</label>
            <p className="mt-1 text-sm text-white">{batch.batch_name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Subject</label>
            <p className="mt-1 text-sm text-white">{batch.subject}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Instructor</label>
            <p className="mt-1 text-sm text-white">{batch.instructor || 'Not assigned'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Maximum Students</label>
            <p className="mt-1 text-sm text-white">{batch.max_students}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Fee Amount</label>
                            <p className="mt-1 text-sm text-white">{batch.fee_amount ? formatIndianCurrency(batch.fee_amount) : 'Not set'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Status</label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
              batch.is_active
                ? 'bg-green-900/30 text-green-300 border border-green-700'
                : 'bg-red-900/30 text-red-300 border border-red-700'
            }`}>
              {batch.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchDetail; 